import { createTheme, PaletteMode } from '@mui/material/styles';

/**
 * Central design tokens for Wualt Admin Portal.
 *
 * Every colour used across the app lives here.  To add dark mode, pass
 * mode = 'dark' – MUI will automatically invert background/text tokens
 * and the overrides below will adapt via the palette references.
 */

// ─── Raw palette values ───────────────────────────────────────────────────────

const BRAND = {
  primary: '#39A9B1',       // teal – buttons, selected indicator, tags
  primaryDark: '#2d8f96',   // hover state
  dark: '#143133',          // sidebar title, deep brand
};

const NEUTRAL = {
  900: '#111827',
  700: '#374151',
  500: '#6B7280',
  400: '#9CA3AF',
  200: '#E5E7EB',
  100: '#F3F4F6',
  50:  '#F9FAFB',
};

const STATUS = {
  successBg:   '#ECFDF5',
  successText: '#60AF97',
  infoBg:      '#EFF6FF',
  infoText:    '#39A9B1',
  errorBg:     '#FEF2F2',
  errorText:   '#DE606C',
};

// ─── Theme factory ────────────────────────────────────────────────────────────

export const buildTheme = (mode: PaletteMode = 'light') =>
  createTheme({
    palette: {
      mode,
      primary: {
        main:  BRAND.primary,
        dark:  BRAND.primaryDark,
        contrastText: '#FFFFFF',
      },
      background: {
        default: mode === 'light' ? '#FFFFFF' : '#0f1923',
        paper:   mode === 'light' ? NEUTRAL[50]  : '#1a2533',
      },
      text: {
        primary:   mode === 'light' ? NEUTRAL[900] : '#F9FAFB',
        secondary: mode === 'light' ? NEUTRAL[500] : '#9CA3AF',
        disabled:  mode === 'light' ? NEUTRAL[400] : '#6B7280',
      },
      divider: mode === 'light' ? NEUTRAL[200] : 'rgba(255,255,255,0.08)',
      error:   { main: STATUS.errorText,   light: STATUS.errorBg   },
      success: { main: STATUS.successText, light: STATUS.successBg },
      info:    { main: STATUS.infoText,    light: STATUS.infoBg    },
    },

    // ── Typography ────────────────────────────────────────────────────────────
    typography: {
      fontFamily: 'Inter, Arial, sans-serif',
      // page heading  e.g. "Operating Nodes"
      h6: { fontWeight: 700, fontSize: '20px', lineHeight: '30px' },
      // section label
      subtitle1: { fontWeight: 600, fontSize: '14px', lineHeight: '21px', letterSpacing: '-0.15px' },
      // body / cell text
      body1: { fontWeight: 400, fontSize: '13px', lineHeight: '19.5px' },
      body2: { fontWeight: 400, fontSize: '12px', lineHeight: '18px' },
      // caption / muted text
      caption: { fontWeight: 400, fontSize: '11px', lineHeight: '16.5px' },
    },

    // ── Component overrides ───────────────────────────────────────────────────
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: mode === 'light' ? '#FFFFFF' : '#0f1923',
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            textTransform: 'none',
            fontFamily: 'Inter, Arial, sans-serif',
            fontWeight: 500,
            fontSize: '13px',
          },
          containedPrimary: {
            backgroundColor: BRAND.primary,
            '&:hover': { backgroundColor: BRAND.primaryDark },
          },
          outlinedPrimary: {
            borderColor: NEUTRAL[200],
            color: NEUTRAL[700],
            '&:hover': { borderColor: BRAND.primary, backgroundColor: 'transparent' },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: NEUTRAL[200],
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: BRAND.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: BRAND.primary,
            },
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          root: { borderRadius: '10px' },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: '12px' },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: { borderRadius: '9999px', fontWeight: 600, fontSize: '10px' },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'light' ? '#FFFFFF' : '#111827',
            borderRight: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#FFFFFF' : '#111827',
            color: mode === 'light' ? NEUTRAL[900] : '#F9FAFB',
            boxShadow: 'none',
            borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: '16px' },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '13px',
          },
        },
      },

    },
  });

// ─── Named token exports ──────────────────────────────────────────────────────
// Use these in styles files instead of raw hex strings so a future palette
// swap only requires editing this file.

export const tokens = {
  brand: BRAND,
  neutral: NEUTRAL,
  status: STATUS,
} as const;

export type AppTokens = typeof tokens;
